import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, GitBranch, ShieldCheck, Sparkles, Loader2, Lock } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const enter = async (e) => {
    e?.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user);
      navigate("/");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid-noise flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-5xl grid lg:grid-cols-2 gap-12 items-center">
        <div className="animate-fade-up">
          <div className="flex items-center gap-2 text-ai mb-6">
            <GitBranch className="w-5 h-5" />
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground">
              VLink Labs
            </span>
          </div>
          <h1 className="font-heading text-4xl sm:text-6xl font-black tracking-tighter leading-none">
            Spark to Scale.
            <br />
            <span className="text-muted-foreground">Gate Control.</span>
          </h1>
          <p className="mt-6 text-muted-foreground text-sm leading-relaxed max-w-md">
            Every idea runs the pipeline with enforced gates, AI agents at each checkpoint, and
            humans owning the high-stakes decisions. No exit criteria skipped. No owner missing.
            Full accountability to "product ready".
          </p>
          <div className="mt-8 flex flex-col gap-3 text-sm">
            <Feature icon={ShieldCheck} text="Hard gate enforcement — nothing moves without sign-off" />
            <Feature icon={Sparkles} text="AI recommends, humans decide on high-stakes gates" />
            <Feature icon={GitBranch} text="Every Approve / Iterate / Reject logged with an owner" />
          </div>
        </div>

        <div className="border border-border bg-card rounded-sm p-6 animate-fade-up" style={{ animationDelay: "0.1s" }}>
          <h2 className="font-heading text-xl font-bold tracking-tight">Admin sign in</h2>
          <p className="text-xs text-muted-foreground mt-1 mb-5 font-mono flex items-center gap-1.5">
            <Lock className="w-3.5 h-3.5" /> ADMIN ACCESS — manage team, gates & readiness
          </p>
          <form onSubmit={enter} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@vlinklabs.com"
                className="mt-1.5 rounded-sm bg-background"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Password</Label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5 rounded-sm bg-background"
              />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-danger">{error}</p>}
            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading || !email || !password}
              className="w-full rounded-sm font-semibold group"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Enter Orchestrator
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-3 text-muted-foreground">
      <Icon className="w-4 h-4 text-ai shrink-0" />
      <span>{text}</span>
    </div>
  );
}
